import { EVENTS } from './events.js';

/**
 * datastore – central storage for all runtime data slices
 * - options: validated configuration (merged patches)
 * - state: runtime flags and positions (merged patches)
 * - widths: measured layout numbers (replaced wholesale)
 * - slides: internal slide descriptors (replaced wholesale)
 *
 * all writes must go through the mutators so events fire.
 * reads return freshly frozen copies to prevent accidental mutation.
 */
export default class DataStore {
	/** @type {any} private emitter */
	#emitter;

	/** @type {object} private live objects (never expose directly) */
	#options;
	#state;
	#widths;
	#slides;
	#transformPoints;
	#animation;
	#visibility;

	/** @type {boolean} private dirty state tracking */
	#isDirty;

	/**
	 * creates a new datastore instance
	 * @param {object} emitter - shared emitter for pub/sub (must implement emit)
	 */
	constructor(emitter) {
		const _ = this;

		// simple emitter validation to avoid silent failures
		if (!emitter || typeof emitter.emit !== 'function') {
			throw new Error('data-store requires an emitter with an emit method');
		}
		_.#emitter = emitter;

		// canonical defaults
		_.#isDirty = false;
		_.#options = {};
		_.#state = {
			selectedIndex: 0,
			renderIndex: 0,
			pageIndex: 0,
			pageCount: 1,
			canLoop: false,
			isDragging: false,
			slideCount: 0,
			// snap points for page navigation (track positions per page)
			snapPoints: [],
			firstSnapPoint: 0,
			lastSnapPoint: 0,
		};
		_.#widths = {
			viewport: 0,
			track: 0,
			slide: 0,
			slideMin: 0,
			gap: 0,
			slideAndGap: 0,
			paddingLeft: 0,
			paddingRight: 0,
			visibilityBoundsWidth: 0,
			visibilityBoundsOffset: 0,
		};
		_.#slides = [];
		_.#transformPoints = {};
		_.#animation = {
			movementType: 'jump', // 'jump', 'animate', 'settle', 'drag', 'momentum', 'scroll'
			trackPosition: 0,
			trackDelta: 0,
			velocity: 0,
			progress: 1,
			isAnimating: false,
			direction: 0, // -1 for left, 1 for right, 0 for no movement/jump
			trackPercent: 0, // spatial progress: 0-1 in-range, may overflow (<0 or >1) during elastic/loop states
		};
		_.#visibility = {
			partiallyVisibleSlides: [], // slides with any visibility (> 0%)
			mostlyVisibleSlides: [], // slides with >= 66% visibility
			fullyVisibleSlides: [], // slides with >= 98% visibility
		};
	}

	// ---------------------------------------------------------------------
	// options slice
	// ---------------------------------------------------------------------

	/**
	 * returns a readonly copy of options
	 * @returns {Readonly<object>}
	 */
	getOptions() {
		// return a frozen shallow copy to prevent external mutation
		return Object.freeze({ ...this.#options });
	}

	/**
	 * merges a patch into options and emits options:changed
	 * @param {object} [patch={}] - partial options to merge
	 */
	setOptions(patch = {}) {
		const _ = this;

		const previousOptions = _.getOptions();
		_.#options = { ..._.#options, ...patch };
		const currentOptions = _.getOptions();

		_.#markAsDirty();

		_.#emitter.emit(EVENTS.store.optionsChanged, {
			previousOptions,
			currentOptions,
		});
	}

	// ---------------------------------------------------------------------
	// state slice
	// ---------------------------------------------------------------------

	/**
	 * returns a readonly copy of state
	 * @returns {Readonly<object>}
	 */
	getState() {
		return Object.freeze({ ...this.#state });
	}

	/**
	 * merges a patch into state, emits state:changed,
	 * and fires fine-grained index events when applicable
	 * @param {object} [patch={}] - partial state updates
	 */
	setState(patch = {}) {
		const _ = this;

		const previousState = _.getState();
		_.#state = { ..._.#state, ...patch };
		const currentState = _.getState();

		_.#markAsDirty();

		// coarse event for any state change
		_.#emitter.emit(EVENTS.store.stateChanged, { previousState, currentState });

		// fine-grained signals for common keys
		if (patch.selectedIndex !== undefined && patch.selectedIndex !== previousState.selectedIndex) {
			_.#emitter.emit(EVENTS.store.selectedIndexChanged, {
				previousIndex: previousState.selectedIndex,
				currentIndex: patch.selectedIndex,
			});
		}

		if (patch.renderIndex !== undefined && patch.renderIndex !== previousState.renderIndex) {
			_.#emitter.emit(EVENTS.store.renderIndexChanged, {
				previousIndex: previousState.renderIndex,
				currentIndex: patch.renderIndex,
			});
		}

		if (patch.pageIndex !== undefined && patch.pageIndex !== previousState.pageIndex) {
			_.#emitter.emit(EVENTS.store.pageIndexChanged, {
				previousPageIndex: previousState.pageIndex,
				currentPageIndex: patch.pageIndex,
			});
		}

		if (patch.pageCount !== undefined && patch.pageCount !== previousState.pageCount) {
			_.#emitter.emit(EVENTS.store.pageCountChanged, {
				count: patch.pageCount,
			});
		}

		if (patch.canLoop !== undefined && patch.canLoop !== previousState.canLoop) {
			_.#emitter.emit(EVENTS.store.canLoopChanged, {
				canLoop: patch.canLoop,
			});
		}

		if (patch.snapPoints !== undefined) {
			_.#emitter.emit(EVENTS.store.snapPointsChanged, {
				snapPoints: patch.snapPoints,
				firstSnapPoint: patch.firstSnapPoint,
				lastSnapPoint: patch.lastSnapPoint,
			});
		}
	}

	// ---------------------------------------------------------------------
	// widths slice
	// ---------------------------------------------------------------------

	/**
	 * returns a readonly copy of widths
	 * @returns {Readonly<object>}
	 */
	getWidths() {
		return Object.freeze({ ...this.#widths });
	}

	/**
	 * replaces all width metrics and emits layout:changed
	 * @param {object} [nextWidths={}] - full set of layout metrics
	 */
	setWidths(nextWidths = {}) {
		const _ = this;

		const previousWidths = _.getWidths();
		_.#widths = { ...nextWidths };
		const currentWidths = _.getWidths();

		_.#markAsDirty();

		_.#emitter.emit(EVENTS.store.layoutChanged, {
			previousWidths,
			currentWidths,
		});
	}

	// ---------------------------------------------------------------------
	// slides slice
	// ---------------------------------------------------------------------

	/**
	 * returns a readonly copy of slides (array)
	 * @returns {Readonly<Array>}
	 */
	getSlides() {
		// freeze the array reference so callers can't push/pop
		return Object.freeze([...this.#slides]);
	}

	/**
	 * replaces the slides array, syncs slideCount in state,
	 * and emits slides:changed
	 * @param {Array} [slides=[]] - new slide descriptors
	 */
	setSlides(slides = []) {
		const _ = this;

		// cache previous and set slides
		const previousSlides = _.getSlides();
		_.#slides = [...slides];
		const currentSlides = _.getSlides();

		// keep slideCount in sync via state setter (also emits fine-grained events)
		_.setState({ slideCount: _.#slides.length });

		_.#markAsDirty();

		_.#emitter.emit(EVENTS.store.slidesChanged, {
			previousSlides,
			currentSlides,
		});
	}

	// ---------------------------------------------------------------------
	// transformPoints slice
	// ---------------------------------------------------------------------

	/**
	 * returns a readonly copy of transformPoints
	 * @returns {Readonly<object>}
	 */
	getTransformPoints() {
		// return frozen copy to prevent mutations
		return Object.freeze({ ...this.#transformPoints });
	}

	/**
	 * replaces the transformPoints object and emits transformPoints:changed
	 * @param {object} [nextPoints={}] - new transform points mapping
	 */
	setTransformPoints(nextPoints = {}) {
		const _ = this;

		// cache previous points
		const previousPoints = _.getTransformPoints();

		// replace transformPoints
		_.#transformPoints = { ...nextPoints };

		_.#markAsDirty();

		// emit event so subscribers can react
		_.#emitter.emit(EVENTS.store.transformPointsChanged, {
			previousPoints,
			currentPoints: Object.freeze({ ..._.#transformPoints }),
		});
	}

	// ---------------------------------------------------------------------
	// animation slice
	// ---------------------------------------------------------------------

	/**
	 * updates animation state for frame-based rendering
	 * @param {object} animationData - animation data
	 * @param {string} animationData.movementType - movement type ('animate' | 'jump' | 'settle' | 'drag' | 'momentum' | 'scroll')
	 * @param {number} [animationData.trackPosition] - current track position
	 * @param {number} [animationData.trackDelta] - change in track position
	 * @param {number} [animationData.velocity] - current velocity
	 * @param {number} [animationData.progress] - animation progress (0-1)
	 * @param {boolean} [animationData.isAnimating] - whether animation is active
	 */
	setAnimation(animationData = {}) {
		const _ = this;

		// capture previous animation data for change detection
		const previousAnimation = { ..._.#animation };

		// merge new animation data with existing structure
		_.#animation = {
			..._.#animation,
			...animationData,
		};

		// calculate trackPercent when trackPosition changes
		if (animationData.trackPosition !== undefined) {
			_.#animation.trackPercent = _.#calculateTrackPercent(animationData.trackPosition);
		}

		_.#markAsDirty();

		// emit fine-grained event for track position changes
		if (animationData.trackPosition !== undefined && animationData.trackPosition !== previousAnimation.trackPosition) {
			_.#emitter.emit(EVENTS.track.positionChanged, {
				previousTrackPosition: previousAnimation.trackPosition,
				currentTrackPosition: animationData.trackPosition,
				trackDelta: (animationData.trackPosition - previousAnimation.trackPosition),
				movementType: animationData.movementType || 'unknown',
				trackPercent: _.#animation.trackPercent,
			});
		}
	}

	/**
	 * calculates the track percent (spatial progress) from track position
	 * @param {number} trackPosition - current track position
	 * @returns {number} progress value: usually 0-1, may overflow (<0 or >1) in elastic/loop states
	 * @private
	 */
	#calculateTrackPercent(trackPosition) {
		const { firstSnapPoint, lastSnapPoint, canLoop } = this.#state;
		const { track: trackWidth } = this.#widths;

		// normal range calculation
		const range = firstSnapPoint - lastSnapPoint; // positive value (first is usually 0 or small negative, last is larger negative)
		if (range === 0) return 0;

		const percent = (firstSnapPoint - trackPosition) / range;

		// non-looping: preserve elastic overflow instead of clamping to 0-1
		if (!canLoop) return percent;

		// looping: allow overflow for loop transitions
		// percent > 1.0 = forward loop transition (moving toward page 0)
		// percent < 0.0 = backward loop transition (moving toward last page)
		if (percent > 1) {
			// forward loop: normalize overflow so it approaches 2.0 at shift point
			const overflowDistance = Math.abs(trackPosition - lastSnapPoint);
			const loopRange = trackWidth - Math.abs(lastSnapPoint - firstSnapPoint);
			if (loopRange <= 0) return percent;
			const normalizedOverflow = overflowDistance / loopRange;
			return 1 + normalizedOverflow;
		}

		if (percent < 0) {
			// backward loop: normalize overflow so it approaches -1.0 at shift point
			const overflowDistance = Math.abs(firstSnapPoint - trackPosition);
			const loopRange = trackWidth - Math.abs(lastSnapPoint - firstSnapPoint);
			if (loopRange <= 0) return percent;
			const normalizedOverflow = overflowDistance / loopRange;
			return -normalizedOverflow;
		}

		return percent;
	}

	/**
	 * returns a readonly copy of animation
	 * @returns {Readonly<object>}
	 */
	getAnimation() {
		return Object.freeze({ ...this.#animation });
	}

	// ---------------------------------------------------------------------
	// visibility slice
	// ---------------------------------------------------------------------

	/**
	 * returns a readonly copy of visibility state
	 * @returns {Readonly<{partiallyVisibleSlides: HTMLElement[], mostlyVisibleSlides: HTMLElement[], fullyVisibleSlides: HTMLElement[]}>}
	 */
	getVisibility() {
		return Object.freeze({
			partiallyVisibleSlides: Object.freeze([...this.#visibility.partiallyVisibleSlides]),
			mostlyVisibleSlides: Object.freeze([...this.#visibility.mostlyVisibleSlides]),
			fullyVisibleSlides: Object.freeze([...this.#visibility.fullyVisibleSlides]),
		});
	}

	/**
	 * updates the visibility state with current visible slides
	 * @param {object} visibility - visibility data
	 * @param {HTMLElement[]} visibility.partiallyVisibleSlides - slides with any visibility (> 0%)
	 * @param {HTMLElement[]} visibility.mostlyVisibleSlides - slides with >= 66% visibility
	 * @param {HTMLElement[]} visibility.fullyVisibleSlides - slides with >= 98% visibility
	 */
	setVisibility({ partiallyVisibleSlides = [], mostlyVisibleSlides = [], fullyVisibleSlides = [] } = {}) {
		const _ = this;

		// check if visibility has actually changed
		const prev = _.#visibility;
		const partiallyChanged =
			partiallyVisibleSlides.length !== prev.partiallyVisibleSlides.length ||
			partiallyVisibleSlides.some((s, i) => s !== prev.partiallyVisibleSlides[i]);
		const mostlyChanged =
			mostlyVisibleSlides.length !== prev.mostlyVisibleSlides.length ||
			mostlyVisibleSlides.some((s, i) => s !== prev.mostlyVisibleSlides[i]);
		const fullyChanged =
			fullyVisibleSlides.length !== prev.fullyVisibleSlides.length ||
			fullyVisibleSlides.some((s, i) => s !== prev.fullyVisibleSlides[i]);

		if (!partiallyChanged && !mostlyChanged && !fullyChanged) return;

		// update visibility
		_.#visibility = {
			partiallyVisibleSlides: [...partiallyVisibleSlides],
			mostlyVisibleSlides: [...mostlyVisibleSlides],
			fullyVisibleSlides: [...fullyVisibleSlides],
		};

		// emit event with slides and indices for convenience
		_.#emitter.emit(EVENTS.slides.visibleChanged, {
			partiallyVisibleSlides: _.#visibility.partiallyVisibleSlides,
			mostlyVisibleSlides: _.#visibility.mostlyVisibleSlides,
			fullyVisibleSlides: _.#visibility.fullyVisibleSlides,
			partiallyVisibleIndices: _.#visibility.partiallyVisibleSlides.map((s) => s._index),
			mostlyVisibleIndices: _.#visibility.mostlyVisibleSlides.map((s) => s._index),
			fullyVisibleIndices: _.#visibility.fullyVisibleSlides.map((s) => s._index),
		});
	}

	// ---------------------------------------------------------------------
	// dirty state tracking
	// ---------------------------------------------------------------------

	/**
	 * returns the current dirty state
	 * @returns {boolean} true if store has pending changes
	 */
	isDirty() {
		return this.#isDirty;
	}

	/**
	 * marks the store as clean (no pending changes)
	 * emits store:changed-clean event
	 */
	markAsClean() {
		if (this.#isDirty) {
			this.#isDirty = false;
			this.#emitter.emit(EVENTS.store.changedClean);
		}
	}

	/**
	 * private method to mark store as dirty and emit event
	 * @private
	 */
	#markAsDirty() {
		if (!this.#isDirty) {
			this.#isDirty = true;
			this.#emitter.emit(EVENTS.store.changedDirty);
		}
	}

	// ---------------------------------------------------------------------
	// snapshot (for frame engine)
	// ---------------------------------------------------------------------

	/**
	 * returns a complete snapshot of all store data for frame rendering
	 * @returns {Readonly<object>} complete frozen snapshot with all data slices
	 */
	getSnapshot() {
		const _ = this;
		return Object.freeze({
			state: _.getState(),
			widths: _.getWidths(),
			slides: _.getSlides(),
			transformPoints: _.getTransformPoints(),
			options: _.getOptions(),
			animation: _.getAnimation(),
			visibility: _.getVisibility(),
		});
	}

	/**
	 * cleans up the data store and clears all references
	 * should be called when the carousel is destroyed to prevent memory leaks
	 */
	destroy() {
		const _ = this;

		// clear emitter reference
		_.#emitter = null;

		// clear all data slices
		_.#options = null;
		_.#state = null;
		_.#widths = null;
		_.#slides = null;
		_.#transformPoints = null;
		_.#animation = null;
		_.#visibility = null;

		// reset dirty state
		_.#isDirty = false;
	}
}
